import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { initConnectionHealth } from '@/stores/connectionHealthStore'
import { probeBackendReachable } from '@/lib/reachability'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import { createIDBPersister } from './lib/persister'
import { setupPosCartBroadcast } from './lib/cartChannel'
import { setupPosSessionBroadcast } from './lib/sessionChannel'
import { initBugsnag, getBugsnagErrorBoundary } from './lib/bugsnag'
import { initPostHog } from './lib/posthog'
import { useCartStore } from './stores/cartStore'
import { useSessionStore } from './stores/sessionStore'
import '@fontsource-variable/inter/index.css'
import './index.css'

// Boot guard reconciliation (see public/boot-guard.js): reaching this line
// means the bundle executed, so the shell is healthy. Clear the one-shot
// reload flag so a FUTURE stale deploy can self-heal again, and strip the
// cache-busting `_r` param BEFORE the router parses the URL so it never
// surfaces as route search state.
// Clear the one-shot reload flag (best-effort; storage may be unavailable).
try {
  sessionStorage.removeItem('__waio_boot_reload__')
} catch {
  // Storage blocked in a locked-down WebView — the `_r` URL marker stripped
  // below is the real loop guard, so this failing is non-fatal.
}
// Strip the cache-busting `_r` param INDEPENDENTLY of storage access. The
// boot-guard reads a lingering `_r` as "already retried once", so if storage
// throws above and this were in the same try block, `_r` would never be
// stripped — making the NEXT stale-shell deploy skip its one self-heal reload
// and jump straight to the fallback. Kept separate, and run BEFORE the router
// parses the URL so `_r` never surfaces as route search state.
try {
  const bootUrl = new URL(window.location.href)
  if (bootUrl.searchParams.has('_r')) {
    bootUrl.searchParams.delete('_r')
    window.history.replaceState(
      window.history.state,
      '',
      bootUrl.pathname + bootUrl.search + bootUrl.hash,
    )
  }
} catch {
  // URL / history access blocked — non-fatal.
}

// Wire the v2 in-memory cart to broadcast on every change so a CFD window
// (same machine, same origin) gets sub-ms updates via BroadcastChannel.
// Idempotent + no-op until cart components actually mutate the store, so
// safe to enable at boot even before the FE wiring PR lands.
setupPosCartBroadcast({
  getCart: () => useCartStore.getState(),
  subscribeToCart: (listener) => useCartStore.subscribe(listener),
})

// Cross-window session sync, additive only: POS publishes a snapshot
// every time it has a fully-formed {token, store, register} that differs
// from the last broadcast. Partial states from the connect/login cascade
// are filtered at the publisher; logout never broadcasts. The CFD's
// view can only ever go up, never down — guarantees no route guard
// can fire as a side effect of cross-window sync.
setupPosSessionBroadcast({
  getInputs: () => {
    const s = useSessionStore.getState()
    return {
      backendUrl: s.backendUrl,
      sessionToken: s.sessionToken,
      selectedStore: s.selectedStore,
      selectedRegister: s.selectedRegister,
      displayLogo: s.displayLogo,
    }
  },
  subscribe: (listener) => useSessionStore.subscribe(listener),
})

// Create IndexedDB persister for offline support
const persister = createIDBPersister()

// Create a new query client with sensible defaults for POS
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Balanced default: short-lived cache; critical flows override per-hook.
      staleTime: 1000 * 30,
      gcTime: 1000 * 60 * 30, // 30 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      networkMode: 'online',
    },
    mutations: {
      retry: 0,
      networkMode: 'online',
    },
  },
})

// Create the router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

// Init Bugsnag BEFORE rendering so the error boundary it provides is
// hooked up to the live client. initBugsnag returns false (and getBugsnag-
// ErrorBoundary returns null) when no API key is configured, so dev /
// preview builds without a key still mount cleanly.
initBugsnag()
const BugsnagErrorBoundary = getBugsnagErrorBoundary()

// PostHog session replay + network capture, after Bugsnag so its
// error-report → replay-URL hook can see the started client. No-op
// without VITE_POSTHOG_PROJECT_TOKEN. Same store-injection pattern as the
// broadcast setups above: lib/posthog stays store-agnostic.
initPostHog({
  getIdentity: () => {
    const s = useSessionStore.getState()
    return { store: s.selectedStore, register: s.selectedRegister }
  },
  subscribe: (listener) => useSessionStore.subscribe(listener),
})

// Register the router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Install connection-health listeners (online/offline events, Chromium
// network-quality changes, the local re-evaluation tick) exactly once.
// The probe checks a browser "offline" against the backend's /status.
initConnectionHealth({ probeReachability: probeBackendReachable })

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 30, // 30 minutes
        buster: import.meta.env.VITE_BUILD_VERSION || 'v2-cache-policy',
        // Never persist mutations. TanStack's default dehydrates PAUSED
        // (offline) mutations with their variables and resumes them on
        // reconnect: a manager PIN (check-manager-access) or a cart body
        // carrying override_pin would sit in IndexedDB and replay later,
        // e.g. opening Settings/Payout unprompted.
        dehydrateOptions: { shouldDehydrateMutation: () => false },
      }}
    >
      {BugsnagErrorBoundary ? (
        <BugsnagErrorBoundary
          FallbackComponent={() => (
            <div className="min-h-screen bg-white flex items-center justify-center p-6">
              <div className="max-w-md text-center">
                <p className="text-lg font-semibold text-gray-900">Something went wrong.</p>
                <p className="text-sm text-gray-600 mt-2">
                  An unexpected error occurred. Refresh the app and try again.
                </p>
              </div>
            </div>
          )}
        >
          <RouterProvider router={router} />
        </BugsnagErrorBoundary>
      ) : (
        <RouterProvider router={router} />
      )}
    </PersistQueryClientProvider>
  </StrictMode>,
)
